import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { CheckCircle, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useConference } from '@/context/ConferenceContext';
import { submitAbstract, getErrorMessage } from '@/services/api';

const submissionSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  title: z.string().min(1, 'Title is required'),
  paperTitle: z.string().min(10, 'Paper title must be at least 10 characters'),
  track: z.string().min(1, 'Please select a track'),
  correspondingEmail: z.string().email('Invalid email'),
  country: z.string().min(1, 'Country is required'),
  phone: z.string().min(9, 'Phone number must be at least 9 digits').max(15, 'Phone number must be at most 15 digits').regex(/^[0-9]+$/, 'Phone number must contain only digits'),
  organization: z.string().min(1, 'Organization is required'),
  address: z.string().min(1, 'Address is required'),
});

const Submission = () => {
  const { conference, shortName, importantDetails, loading: confLoading, isAbstractSubmissionOpen, siteConfig } = useConference();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceId, setReferenceId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    resolver: zodResolver(submissionSchema),
  });

  const onSubmit = async (data) => {
    if (!selectedFile) {
      toast.error('Please upload your abstract file');
      return;
    }

    try {
      const formData = new FormData();
      
      // Add all form fields matching backend expectations
      formData.append('user', shortName);
      formData.append('title', data.title);
      formData.append('fname', data.name);
      formData.append('country', data.country);
      formData.append('org', data.organization);
      formData.append('email', data.correspondingEmail);
      formData.append('phno', data.phone);
      formData.append('category', data.track);
      formData.append('trackName', data.track);
      formData.append('address', data.address);
      formData.append('presentationTitle', data.paperTitle);
      formData.append('entity', ''); // Optional
      
      // Add file
      formData.append('file', selectedFile);

      const response = await submitAbstract(formData);
      setReferenceId(response.id || response.submissionId || 'PENDING');
      setIsSubmitted(true);
      toast.success(response.message || 'Abstract submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF, DOC, or DOCX file');
        e.target.value = '';
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      toast.success(`File "${file.name}" selected`);
    }
  };

  if (confLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAbstractSubmissionOpen()) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Submission Closed</h2>
          <p className="text-gray-600 mb-6">
            Abstract submission deadline has passed for this conference.
          </p>
          <Button onClick={() => window.location.href = '/'}>Return to Home</Button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Abstract Submitted Successfully - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        </Helmet>
        <div className="min-h-screen bg-white flex items-center justify-center py-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex w-20 h-20 rounded-full bg-green-100 items-center justify-center mb-6">
              <CheckCircle className="text-green-500" size={48} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Abstract Submitted Successfully!</h1>
            <p className="text-lg text-gray-600 mb-6">
              Thank you for submitting your abstract to CCAI-2026.
            </p>
            <div className="bg-gray-50 p-6 rounded-2xl mb-8">
              <p className="text-sm text-gray-600 mb-2">Your Reference ID</p>
              <p className="text-2xl font-bold text-sky-500" data-testid="reference-id">{referenceId}</p>
              <p className="text-sm text-gray-500 mt-3">
                Please save this reference ID for future correspondence.
              </p>
            </div>
            <div className="space-y-3 text-left bg-white p-6 rounded-2xl border-2 border-gray-100">
              <h3 className="font-semibold text-gray-900 text-center mb-4">What Happens Next?</h3>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-bold">1</span>
                </div>
                <p className="text-gray-600">You will receive a confirmation email within 24 hours</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-bold">2</span>
                </div>
                <p className="text-gray-600">Your abstract will undergo peer review</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-sky-600 text-sm font-bold">3</span>
                </div>
                <p className="text-gray-600">
                  You will be notified of acceptance by {siteConfig?.dates?.notification || importantDetails?.notification_date || ''}
                </p>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              className="mt-8 bg-sky-500 hover:bg-sky-600"
              data-testid="return-home-btn"
            >
              Return to Home
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Submit Abstract - {importantDetails?.ConferenceTitle || siteConfig?.name || 'Conference'}</title>
        <meta name="description" content="Submit your research abstract to CCAI-2026" />
      </Helmet>

      <div className="bg-white min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Submit Your Abstract</h1>
            <p className="text-gray-600">
              Complete the form below to submit your research abstract for CCAI-2026
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="submission-form">
            {/* Paper Details */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Paper Details</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder="Your full name"
                    className="mt-1"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Select onValueChange={(value) => setValue('title', value)}>
                    <SelectTrigger className="mt-1" data-testid="title-select">
                      <SelectValue placeholder="Select your title" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr.">Mr.</SelectItem>
                      <SelectItem value="Mrs.">Mrs.</SelectItem>
                      <SelectItem value="Ms.">Ms.</SelectItem>
                      <SelectItem value="Dr.">Dr.</SelectItem>
                      <SelectItem value="Prof.">Prof.</SelectItem>
                      <SelectItem value="Engr.">Engr.</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="paperTitle">Paper Title *</Label>
                  <Input
                    id="paperTitle"
                    {...register('paperTitle')}
                    placeholder="Enter your paper title"
                    className="mt-1"
                  />
                  {errors.paperTitle && (
                    <p className="text-red-500 text-sm mt-1">{errors.paperTitle.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="track">Track *</Label>
                  <Select onValueChange={(value) => setValue('track', value)}>
                    <SelectTrigger className="mt-1" data-testid="track-select">
                      <SelectValue placeholder="Select a track" />
                    </SelectTrigger>
                    <SelectContent>
                      {(siteConfig?.tracks || [
                        { id: 'structural', name: 'Structural Engineering' },
                        { id: 'geotechnical', name: 'Geotechnical Engineering' },
                        { id: 'transportation', name: 'Transportation Engineering' },
                        { id: 'water', name: 'Water Resources' },
                        { id: 'construction', name: 'Construction Management' },
                        { id: 'environmental', name: 'Environmental Engineering' },
                      ]).map((track) => (
                        <SelectItem key={track.id} value={track.id}>
                          {track.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.track && (
                    <p className="text-red-500 text-sm mt-1">{errors.track.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Details</h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="correspondingEmail">Email *</Label>
                  <Input
                    id="correspondingEmail"
                    {...register('correspondingEmail')}
                    type="email"
                    placeholder="email@example.com"
                    className="mt-1"
                    data-testid="corresponding-email-input"
                  />
                  {errors.correspondingEmail && (
                    <p className="text-red-500 text-sm mt-1">{errors.correspondingEmail.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    {...register('country')}
                    placeholder="Your country"
                    className="mt-1"
                  />
                  {errors.country && (
                    <p className="text-red-500 text-sm mt-1">{errors.country.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    {...register('phone')}
                    placeholder="Your phone number"
                    className="mt-1"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="organization">Organization *</Label>
                  <Input
                    id="organization"
                    {...register('organization')}
                    placeholder="Your organization"
                    className="mt-1"
                  />
                  {errors.organization && (
                    <p className="text-red-500 text-sm mt-1">{errors.organization.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Textarea
                    id="address"
                    {...register('address')}
                    placeholder="Your address"
                    className="mt-1"
                  />
                  {errors.address && (
                    <p className="text-red-500 text-sm mt-1">{errors.address.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Abstract Document * <span className="text-red-500 text-sm">(Required)</span>
              </h2>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-sky-500 transition-colors">
                <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-600 mb-2">Upload your abstract document</p>
                <p className="text-sm text-gray-500 mb-4">PDF, DOC, or DOCX up to 10MB</p>
                <input
                  type="file"
                  id="file-upload"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label htmlFor="file-upload">
                  <Button type="button" variant="outline" className="cursor-pointer" asChild>
                    <span>{selectedFile ? selectedFile.name : 'Choose File'}</span>
                  </Button>
                </label>
                {selectedFile && (
                  <p className="text-green-600 text-sm mt-3">
                    ✓ File selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-sky-500 hover:bg-sky-600 px-12 py-3 text-lg"
                data-testid="submit-abstract-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Abstract'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Submission;
